import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import { ArrowSquareOut, ArrowsClockwise } from '@phosphor-icons/react';

import { ApiError } from '@/api/client';
import {
  assignTeamCategory,
  updateTeam,
  type Team,
} from '@/api/teams';
import { listTeamCategories } from '@/api/teamCategories';
import { listMyOrganizations } from '@/api/orgs';
import { SignupConfigPanel } from '@/components/SignupConfigPanel';
import { TeamRulesPanel } from '@/components/TeamRulesPanel';
import { PLATFORM_LANGUAGES } from '@/components/LanguagesField';
import type { TeamPanelHandle } from '@/components/teamPanelHandle';
import { TeamNoticesPanel } from '@/components/notices/TeamNoticesPanel';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { resolve } from '@/lib/teamColor';

// TeamSheet is the edit surface for a single team. Right-side Sheet
// (matches MemberCard) with three tabs:
//
//   - Details        — name / description / category / color / public-visibility
//   - Schedule rules — per-team override of category scheduling rules
//   - Signup settings — per-team signup_* config (Anmälningar)
//
// Create flow stays in TeamModal (a centered dialog) — a sheet for an
// empty form would feel heavy, and there's nothing to put in the other
// tabs before the team exists. Edit flow funnels exclusively through this
// component.

export interface TeamSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  team: Team | null;
  // Which tab to open on first render. Useful when the Anmälningar page
  // deep-links into "Signup settings" for a specific team. Defaults to
  // 'details'.
  initialTab?: 'details' | 'rules' | 'signups' | 'notices';
}

export function TeamSheet({
  open,
  onOpenChange,
  slug,
  team,
  initialTab = 'details',
}: TeamSheetProps) {
  const { t } = useTranslation('common');
  const contentRef = useRef<HTMLDivElement>(null);
  const [tab, setTab] = useState<string>(initialTab);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // One ref per tab so the sheet's footer Save button can drive all three
  // commits in parallel. Each panel manages its own state + validation;
  // the sheet just orchestrates.
  const detailsRef = useRef<TeamPanelHandle>(null);
  const rulesRef = useRef<TeamPanelHandle>(null);
  const signupsRef = useRef<TeamPanelHandle>(null);
  const noticesRef = useRef<TeamPanelHandle>(null);

  // Reset to the requested initial tab whenever the sheet opens or the
  // entry-point's intent changes (different team, different deep link).
  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setSaveError(null);
    }
  }, [open, initialTab, team?.id]);

  async function saveAll() {
    setSaving(true);
    setSaveError(null);
    try {
      // Run all three commits in parallel. Use allSettled so one
      // failure doesn't swallow the others' outcomes — we want the
      // user to see if some tabs saved while others bounced.
      const results = await Promise.allSettled([
        detailsRef.current?.commit() ?? Promise.resolve(true),
        rulesRef.current?.commit() ?? Promise.resolve(true),
        signupsRef.current?.commit() ?? Promise.resolve(true),
        noticesRef.current?.commit() ?? Promise.resolve(true),
      ]);
      const allOk = results.every(
        (r) => r.status === 'fulfilled' && r.value === true,
      );
      if (allOk) {
        onOpenChange(false);
        return;
      }
      // Find the first failing tab so we can switch the user to it.
      // Validation-rejected commits return false (their panel surfaces
      // its own field-level error); thrown errors come from the server.
      const tabIDs = ['details', 'rules', 'signups', 'notices'] as const;
      const failedIdx = results.findIndex(
        (r) => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === false),
      );
      if (failedIdx >= 0) {
        const failedTab = tabIDs[failedIdx];
        if (failedTab) setTab(failedTab);
      }
      const firstThrown = results.find((r) => r.status === 'rejected');
      if (firstThrown && firstThrown.status === 'rejected') {
        const err = firstThrown.reason;
        setSaveError(err instanceof ApiError ? err.code : 'unknown');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        ref={contentRef}
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl"
      >
        <SheetTitle className="sr-only">
          {team
            ? t('teamSheet.title', { name: team.name })
            : t('teamSheet.titleFallback')}
        </SheetTitle>

        {team ? (
          <>
            <div className="flex flex-1 flex-col gap-5 overflow-y-auto p-4 pt-12 sm:p-6 sm:pt-12">
              <TeamSheetHeader team={team} />

              <Tabs
                value={tab}
                onValueChange={setTab}
                className="flex flex-col gap-5"
              >
                <TabsList className="self-start">
                  <TabsTrigger value="details">
                    {t('teamSheet.tab.details')}
                  </TabsTrigger>
                  <TabsTrigger value="rules">
                    {t('teamSheet.tab.rules')}
                  </TabsTrigger>
                  <TabsTrigger value="signups">
                    {t('teamSheet.tab.signups')}
                  </TabsTrigger>
                  <TabsTrigger value="notices">
                    {t('teamSheet.tab.notices')}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="details">
                  <TeamDetailsForm
                    ref={detailsRef}
                    slug={slug}
                    team={team}
                    selectPortalContainer={contentRef.current}
                  />
                </TabsContent>

                <TabsContent value="rules">
                  <TeamRulesPanel
                    ref={rulesRef}
                    slug={slug}
                    team={team}
                    selectPortalContainer={contentRef.current}
                  />
                </TabsContent>

                <TabsContent value="signups">
                  <SignupConfigPanel
                    ref={signupsRef}
                    slug={slug}
                    teamID={team.id}
                    canManage={true}
                    selectPortalContainer={contentRef.current}
                  />
                </TabsContent>

                <TabsContent value="notices">
                  <TeamNoticesPanel ref={noticesRef} slug={slug} teamID={team.id} />
                </TabsContent>
              </Tabs>
            </div>

            <footer className="flex items-center justify-end gap-3 border-t border-border bg-background p-4 sm:p-6">
              {saveError && (
                <p role="alert" className="mr-auto text-sm text-destructive">
                  {t(`teams.modal.error.${saveError}`, {
                    defaultValue: t('teams.modal.error.unknown'),
                  })}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                {t('common.cancel')}
              </Button>
              <Button type="button" onClick={() => void saveAll()} disabled={saving}>
                {saving ? t('common.submitting') : t('common.save')}
              </Button>
            </footer>
          </>
        ) : (
          <p className="px-4 pt-12 text-sm text-muted-foreground">
            {t('common.loading')}
          </p>
        )}
      </SheetContent>
    </Sheet>
  );
}

function TeamSheetHeader({ team }: { team: Team }) {
  const palette = resolve(team.name, team.color || null);
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-pill"
        style={{ background: palette.bg, border: `2px solid ${palette.border}` }}
      >
        <span
          className="h-3 w-3 rounded-pill"
          style={{ background: palette.dot }}
        />
      </span>
      <div className="flex flex-col">
        <h2 className="font-heading text-xl font-bold tracking-tight">
          {team.name}
        </h2>
        {team.category_name && (
          <span className="text-xs text-muted-foreground">
            {team.category_name}
          </span>
        )}
      </div>
    </div>
  );
}

// TeamDetailsForm is the "Details" tab — the same fields TeamModal owned
// for edit mode (name, description, category, color, public visibility).
// Create mode stays in TeamModal. Exposes a commit handle so the sheet's
// footer Save button can persist this tab alongside the other two.
const TeamDetailsForm = forwardRef<
  TeamPanelHandle,
  {
    slug: string;
    team: Team;
    selectPortalContainer?: HTMLElement | null | undefined;
  }
>(function TeamDetailsForm({ slug, team, selectPortalContainer }, ref) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();
  const { slug: routeSlug } = useParams<{ slug: string }>();
  const manageCategoriesHref = `/orgs/${encodeURIComponent(routeSlug ?? slug)}/settings/team-categories`;

  const [name, setName] = useState(team.name);
  const [description, setDescription] = useState(team.description);
  const [colorOverride, setColorOverride] = useState(team.color ?? '');
  const [publicVisibility, setPublicVisibility] = useState(
    team.public_visibility,
  );
  const [categoryID, setCategoryID] = useState(team.category_id ?? '');
  const [descTranslations, setDescTranslations] = useState<Record<string, string>>(
    team.description_translations ?? {},
  );
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // The org's own language — already cached by the shell under ['my-orgs']
  // (GET /api/v1/me/organizations carries language_code), so this reuses the
  // cache rather than fetching. Used as the description's source language when
  // the team hasn't recorded one yet, so translation targets exclude the
  // language the description is actually written in.
  const orgsQuery = useQuery({
    queryKey: ['my-orgs'],
    queryFn: () => listMyOrganizations(token),
    enabled: !!token,
  });
  const orgLang = orgsQuery.data?.find((o) => o.slug === slug)?.language_code;

  // The language the base description is written in (the team's recorded
  // source, else the org language, else the platform default — matching the
  // backend fallback). Other platform languages are offered as targets.
  const descSourceLang =
    team.description_source_language || orgLang || PLATFORM_LANGUAGES[0]!.code;
  const descTargetLangs = useMemo(
    () => PLATFORM_LANGUAGES.filter((l) => l.code !== descSourceLang),
    [descSourceLang],
  );

  // Reset on team switch.
  useEffect(() => {
    setName(team.name);
    setDescription(team.description);
    setColorOverride(team.color ?? '');
    setPublicVisibility(team.public_visibility);
    setCategoryID(team.category_id ?? '');
    setDescTranslations(team.description_translations ?? {});
    setErrorCode(null);
  }, [team]);

  const categoriesQuery = useQuery({
    queryKey: ['team-categories', slug],
    queryFn: () => listTeamCategories(token, slug),
    enabled: !!token && !!slug,
  });
  const categories = useMemo(
    () => categoriesQuery.data?.categories ?? [],
    [categoriesQuery.data],
  );

  const palette = useMemo(
    () => resolve(name, colorOverride || null),
    [name, colorOverride],
  );

  const isHexValid =
    colorOverride === '' || /^#[0-9a-f]{6}$/i.test(colorOverride.trim());

  // Imperative commit — called by TeamSheet's footer Save button.
  // Returns false when local validation refuses the save so the sheet
  // can switch the user back to this tab; throws on server failure.
  useImperativeHandle(
    ref,
    () => ({
      commit: async () => {
        if (name.trim().length === 0 || !isHexValid) {
          setErrorCode(name.trim().length === 0 ? 'name_required' : 'color_invalid');
          return false;
        }
        setErrorCode(null);
        try {
          // Drop blank translations so we never persist an empty string for a
          // language (the renderer treats blank as "fall back to source").
          const cleanedTranslations: Record<string, string> = {};
          for (const { code } of descTargetLangs) {
            const v = (descTranslations[code] ?? '').trim();
            if (v !== '') cleanedTranslations[code] = v;
          }
          const payload = {
            name: name.trim(),
            description: description.trim(),
            color: colorOverride.trim().toLowerCase(),
            public_visibility: publicVisibility,
            description_source_language: descSourceLang,
            description_translations: cleanedTranslations,
          };
          await updateTeam(token, slug, team.id, payload);
          if (categoryID !== (team.category_id ?? '')) {
            await assignTeamCategory(token, slug, team.id, categoryID);
          }
          void queryClient.invalidateQueries({ queryKey: ['teams', slug] });
          void queryClient.invalidateQueries({ queryKey: ['members', slug] });
          void queryClient.invalidateQueries({
            queryKey: ['team-categories', slug],
          });
          // The Signups page table renders name/color/category from the
          // signup-summary endpoint, not the teams list.
          void queryClient.invalidateQueries({
            queryKey: ['signup-summary', slug],
          });
          return true;
        } catch (err) {
          setErrorCode(err instanceof ApiError ? err.code : 'unknown');
          throw err;
        }
      },
    }),
    [
      name,
      description,
      colorOverride,
      publicVisibility,
      categoryID,
      descTranslations,
      descTargetLangs,
      descSourceLang,
      isHexValid,
      slug,
      team.id,
      team.category_id,
      token,
      queryClient,
    ],
  );

  return (
    <div className="flex flex-col gap-4">
      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('teams.modal.name')}
        <Input
          type="text"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setErrorCode(null);
          }}
          required
          maxLength={120}
          autoComplete="off"
          spellCheck={false}
          placeholder={t('teams.modal.namePlaceholder')}
        />
      </label>

      <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        {t('teams.modal.description')}
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={1000}
          rows={3}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          placeholder={t('teams.modal.descriptionPlaceholder')}
        />
      </label>

      <details className="rounded-md border border-input bg-background/50 px-3 py-2">
        <summary className="cursor-pointer text-sm font-medium text-foreground">
          {t('teams.modal.translations.summary')}
        </summary>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('teams.modal.translations.body')}
        </p>
        <div className="mt-3 flex flex-col gap-3">
          {descTargetLangs.map(({ code, endonym }) => (
            <label
              key={code}
              className="flex flex-col gap-1.5 text-sm font-medium text-foreground"
            >
              <span lang={code}>{endonym}</span>
              <textarea
                value={descTranslations[code] ?? ''}
                onChange={(e) =>
                  setDescTranslations((curr) => ({ ...curr, [code]: e.target.value }))
                }
                maxLength={1000}
                rows={2}
                lang={code}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                placeholder={description}
              />
            </label>
          ))}
        </div>
      </details>

      <div className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
        <label htmlFor="team-sheet-category">{t('teams.modal.category')}</label>
        <Select
          value={categoryID === '' ? '__none__' : categoryID}
          onValueChange={(v) => setCategoryID(v === '__none__' ? '' : v)}
        >
          <SelectTrigger id="team-sheet-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            {...(selectPortalContainer
              ? { container: selectPortalContainer }
              : {})}
          >
            <SelectItem value="__none__">
              {t('teams.modal.categoryNone')}
            </SelectItem>
            {categories.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-normal text-muted-foreground">
            {t('teams.modal.categoryHelp')}
          </span>
          <a
            href={manageCategoriesHref}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex shrink-0 items-center gap-1 rounded-pill px-2 py-1 text-xs font-medium text-foreground/80 underline-offset-2 hover:bg-muted hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('teams.modal.categoryManage')}
            <ArrowSquareOut className="h-3 w-3" aria-hidden weight="bold" />
          </a>
        </div>
      </div>

      <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
        <legend className="px-1 text-sm font-medium text-foreground">
          {t('teams.modal.color')}
        </legend>
        <p className="text-xs text-muted-foreground">
          {colorOverride
            ? t('teams.modal.colorOverridden')
            : t('teams.modal.colorAuto')}
        </p>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={colorOverride || palette.hex}
            onChange={(e) => {
              setColorOverride(e.target.value.toLowerCase());
              setErrorCode(null);
            }}
            aria-label={t('teams.modal.colorPicker')}
            className="h-9 w-12 cursor-pointer rounded-md border border-input bg-background"
          />
          <Input
            type="text"
            value={colorOverride}
            onChange={(e) => {
              setColorOverride(e.target.value.toLowerCase());
              setErrorCode(null);
            }}
            placeholder={palette.hex}
            maxLength={7}
            spellCheck={false}
            className="flex-1 font-mono"
            aria-invalid={!isHexValid || undefined}
          />
          {colorOverride && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setColorOverride('')}
              title={t('teams.modal.colorReset')}
            >
              <ArrowsClockwise className="h-4 w-4" />
              {t('teams.modal.colorReset')}
            </Button>
          )}
        </div>
        {!isHexValid && (
          <p className="text-xs text-destructive">
            {t('teams.modal.colorInvalid')}
          </p>
        )}
      </fieldset>

      <fieldset className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3">
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={publicVisibility}
            onChange={(e) => setPublicVisibility(e.target.checked)}
            className="mt-0.5"
          />
          <span className="flex flex-col gap-1">
            <span className="font-medium text-foreground">
              {t('teams.modal.publicVisibility.label')}
            </span>
            <span className="text-xs text-muted-foreground">
              {t('teams.modal.publicVisibility.help')}
            </span>
          </span>
        </label>
      </fieldset>

      {errorCode && (
        <p role="alert" className="text-sm text-destructive">
          {t(`teams.modal.error.${errorCode}`, {
            defaultValue: t('teams.modal.error.unknown'),
          })}
        </p>
      )}
    </div>
  );
});
